import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Link,
  Checkbox,
  VStack,
  Box,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabasePublic } from "./supabaseClient";
import { getTermsSummary } from "../utils/termsContent";

const TermsOfServiceModal = ({ isOpen, onClose, onAccept }) => {
  const [accepted, setAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [termsSummary, setTermsSummary] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  // Fetch user role on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const { data: { user } } = await supabasePublic().auth.getUser();
        if (user) {
          const { data, error } = await supabasePublic()
            .from('users')
            .select('role')
            .eq('id', user.id)
            .single();

          if (data && !error) {
            setUserRole(data.role);
            const summary = getTermsSummary(data.role);
            setTermsSummary(summary);
          }
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    if (isOpen) {
      fetchUserRole();
    }
  }, [isOpen]);

  const handleAccept = async () => {
    if (!accepted) {
      toast({
        title: "Acceptance Required",
        description: "Please check that you have read and agreed to the Terms of Service",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabasePublic().auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Update the user's terms_accepted status in the database (public schema)
      const { error } = await supabasePublic()
        .from("users")
        .update({ terms_accepted: true })
        .eq("id", user.id);

      if (error) {
        // If column doesn't exist, still accept terms but store in localStorage only
        if (error.code === '42703' || error.message?.includes('does not exist')) {
          console.warn("terms_accepted column doesn't exist - storing acceptance in localStorage only");
          localStorage.setItem("termsAccepted", "true");
          localStorage.setItem("termsAcceptedDate", new Date().toISOString());
        } else {
          throw error;
        }
      } else {
        // Also store in localStorage as a backup/cache
        localStorage.setItem("termsAccepted", "true");
        localStorage.setItem("termsAcceptedDate", new Date().toISOString());
      }
      
      // Wait a moment to ensure database update is committed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (onAccept) {
        onAccept();
      }
      onClose();
      
      toast({
        title: "Terms Accepted",
        description: "Thank you for accepting our Terms of Service.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving terms acceptance:", error);
      toast({
        title: "Error",
        description: "Failed to save your acceptance. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Terms of Service and Privacy Policy</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              {termsSummary ? termsSummary.welcome : "Welcome to Magic Notes! Please review and accept our Terms of Service and Privacy Policy to continue."}
            </Text>
            <Box
              maxH="200px"
              overflowY="auto"
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.50"
            >
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                {termsSummary ? termsSummary.title : "By using Magic Notes, you agree to:"}
              </Text>
              <VStack align="stretch" spacing={2.5} fontSize="sm" pl={2}>
                {termsSummary ? (
                  termsSummary.items.map((item, idx) => (
                    <Text key={idx}>• {item}</Text>
                  ))
                ) : (
                  <>
                    <Text>• Use the service only for lawful purposes</Text>
                    <Text>• Maintain the confidentiality of your account</Text>
                    <Text>• Comply with all applicable privacy laws</Text>
                    <Text>• Review all generated notes for accuracy</Text>
                    <Text>• Obtain necessary consents before recording</Text>
                  </>
                )}
              </VStack>
            </Box>
            <Box
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.50"
            >
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                Privacy Policy — Key Points:
              </Text>
              <VStack align="stretch" spacing={2.5} fontSize="sm" pl={2}>
                <Text>• We collect only the information necessary to provide and improve our service, including account details and usage data.</Text>
                <Text>• Audio recordings and generated notes are processed solely to provide the transcription and documentation features you request.</Text>
                <Text>• We do not sell, rent, or share your personal information or patient data with third parties for marketing purposes.</Text>
                <Text>• Data is stored securely using industry-standard encryption at rest and in transit.</Text>
                <Text>• You may request deletion of your account and associated data at any time by contacting support@magicnotes.ai.</Text>
                <Text>• We may share data with trusted service providers (e.g., cloud infrastructure) under strict confidentiality obligations, and with a signed Business Associate Agreement (BAA) where required by HIPAA.</Text>
                <Text>• We retain your data only as long as necessary to provide the service or as required by applicable law.</Text>
                <Text>• We may update this Privacy Policy and will notify you of material changes via email or in-app notice.</Text>
              </VStack>
            </Box>
            <Box
              p={3}
              bg="blue.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="blue.200"
            >
              <Text fontSize="xs" color="gray.600">
                <strong>Note:</strong> You can review the full Terms of Service and Privacy Policy at any time by clicking on your user profile icon in the top right corner, then selecting "Terms of Service" from the menu.
              </Text>
            </Box>
            <Checkbox
              isChecked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              colorScheme="blue"
            >
              <Text fontSize="sm">
                I have read and agree to the{" "}
                <Link
                  href="/terms-of-service"
                  color="blue.500"
                  textDecoration="underline"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/terms-of-service");
                    onClose();
                  }}
                >
                  Terms of Service and Privacy Policy
                </Link>
              </Text>
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleAccept}
            isLoading={isSaving}
            loadingText="Saving..."
            isDisabled={!accepted || isSaving}
          >
            Accept & Continue
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TermsOfServiceModal;